<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegisterEntryHeader',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits([
    'reload',
    'close',
    'save',
    'registerChanged',
    'deleted',
  ]);

  const props = defineProps({
    entry: {
      type: Object,
      required: true,
    },
    selectRegister: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    newTransaction: $fn.value.pgettext('Register', 'New entry'),
  });
  const registerInfo = ref(null);
  const ux = ref([]);
  const b_6917646200545441_style = computed(() => ({
    'font-size': '1em',
  }));
  const b_6424600534600827_style = computed(() => ({
    'font-size': '1em',
  }));

  const $data = computed(() => ({
    messages: messages.value,
    registerInfo: registerInfo.value,
    ux: ux.value,
    b_6917646200545441_style: b_6917646200545441_style.value,
    b_6424600534600827_style: b_6424600534600827_style.value,
  }));

  const $$load_registerInfo = async () => {
    return {
      selectedRegisterId: props.entry.version.register_id,
      selectedVersionId: props.entry.version.version_id,
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'registerInfo' },
      { name: 'ux' },
      { name: 'b_6917646200545441_style', nodeId: '6917646200545441' },
      { name: 'b_6424600534600827_style', nodeId: '6424600534600827' },
    ],
    loaders: { registerInfo: $$load_registerInfo },
    ux: {},
    dataRefs: {
      messages,
      registerInfo,
      ux,
      b_6917646200545441_style,
      b_6424600534600827_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    return !props.selectRegister && props.entry.version.name;
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return props.entry.isNew
        ? messages.value.newTransaction + ` - ${props.entry.entry_number}`
        : props.entry.entry_number;
    }
    return null;
  });

  watch(
    () => props.entry,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('entry');
    }
  );
  watch(
    () => props.selectRegister,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('selectRegister');
    }
  );

  const act_8358196963355853_script_4z7ixb2ix_value_callable = async (
    $event
  ) => {
    return emit('registerChanged', $event.data);
  };
  const act_8358196963355853_script_4z7ixb2ix_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8358196963355853_script_4z7ixb2ix_value_callable($event);
      },
    };
  };
  const act_4576710983066270_emit_s1nwxbji4_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2124730035910225_emit_mpd4ampup_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2124730035910225_reloadSlickgrid_2p96av3xi_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2124730035910225_request_jinr694i6_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_entry',
      data: {
        id: props.entry.id,
        etag: props.entry.etag,
        locked: true,
      },
    };
  };
  const act_1972474632001399_emit_cshtudcio_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_1972474632001399_reloadSlickgrid_2togvhpfy_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_1972474632001399_request_gj7ursoej_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_entry',
      data: {
        id: props.entry.id,
        etag: props.entry.etag,
        locked: false,
      },
    };
  };
  const act_2383909669375364_emit_sfmiqir83_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2383909669375364_reloadSlickgrid_rgjfwu6bm_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2383909669375364_request_5yr2g6jlk_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_entry',
      data: {
        id: props.entry.id,
        etag: props.entry.etag,
        void: true,
      },
    };
  };
  const act_2342151935932928_emit_nwqgzgqnz_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2342151935932928_reloadSlickgrid_5qwsxmy12_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2342151935932928_request_3v7dv6jzs_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_entry',
      data: {
        id: props.entry.id,
        etag: props.entry.etag,
        void: false,
      },
    };
  };
  const act_2498310591257107_emit_1evtxgqge_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_8358196963355853_script_4z7ixb2ix = new actionTypes.ScriptAction({
    actionArgs: act_8358196963355853_script_4z7ixb2ix_getActionArgs,
  });
  const inst_evh_8358196963355853_updateModelValue_gyy5zixiz =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_script_4z7ixb2ix,
      event: 'update:modelValue',
      displayName: 'script',
    });
  const inst_evh_8358196963355853_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8358196963355853_updateModelValue_gyy5zixiz],
      event: 'update:modelValue',
    });
  const act_4576710983066270_emit_s1nwxbji4 = new actionTypes.EmitAction({
    actionArgs: act_4576710983066270_emit_s1nwxbji4_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_4576710983066270_clickPreventStop_qrbtwjqdn =
    new actionTypes.EventHandlerImpl({
      action: act_4576710983066270_emit_s1nwxbji4,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4576710983066270_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4576710983066270_clickPreventStop_qrbtwjqdn],
      event: 'click.prevent.stop',
    });
  const act_2124730035910225_emit_mpd4ampup = new actionTypes.EmitAction({
    actionArgs: act_2124730035910225_emit_mpd4ampup_getActionArgs,
  });
  const inst_evh_2124730035910225_success_jsdoxuazv =
    new actionTypes.EventHandlerImpl({
      action: act_2124730035910225_emit_mpd4ampup,
      event: 'success',
      displayName: 'emit',
    });
  const act_2124730035910225_reloadSlickgrid_2p96av3xi =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2124730035910225_reloadSlickgrid_2p96av3xi_getActionArgs,
    });
  const inst_evh_2124730035910225_success_11v4dpr14 =
    new actionTypes.EventHandlerImpl({
      action: act_2124730035910225_reloadSlickgrid_2p96av3xi,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2124730035910225_request_jinr694i6 = new actionTypes.RequestAction({
    actionArgs: act_2124730035910225_request_jinr694i6_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_2124730035910225_success_jsdoxuazv,
      inst_evh_2124730035910225_success_11v4dpr14,
    ],
  });
  const inst_evh_2124730035910225_clickPreventStop_6a3050pj9 =
    new actionTypes.EventHandlerImpl({
      action: act_2124730035910225_request_jinr694i6,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_2124730035910225_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2124730035910225_clickPreventStop_6a3050pj9],
      event: 'click.prevent.stop',
    });
  const act_1972474632001399_emit_cshtudcio = new actionTypes.EmitAction({
    actionArgs: act_1972474632001399_emit_cshtudcio_getActionArgs,
  });
  const inst_evh_1972474632001399_success_ovmceg5ns =
    new actionTypes.EventHandlerImpl({
      action: act_1972474632001399_emit_cshtudcio,
      event: 'success',
      displayName: 'emit',
    });
  const act_1972474632001399_reloadSlickgrid_2togvhpfy =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1972474632001399_reloadSlickgrid_2togvhpfy_getActionArgs,
    });
  const inst_evh_1972474632001399_success_ggz3hqip4 =
    new actionTypes.EventHandlerImpl({
      action: act_1972474632001399_reloadSlickgrid_2togvhpfy,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1972474632001399_request_gj7ursoej = new actionTypes.RequestAction({
    actionArgs: act_1972474632001399_request_gj7ursoej_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_1972474632001399_success_ovmceg5ns,
      inst_evh_1972474632001399_success_ggz3hqip4,
    ],
  });
  const inst_evh_1972474632001399_clickPreventStop_z12nj6cim =
    new actionTypes.EventHandlerImpl({
      action: act_1972474632001399_request_gj7ursoej,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_1972474632001399_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1972474632001399_clickPreventStop_z12nj6cim],
      event: 'click.prevent.stop',
    });
  const act_2383909669375364_emit_sfmiqir83 = new actionTypes.EmitAction({
    actionArgs: act_2383909669375364_emit_sfmiqir83_getActionArgs,
  });
  const inst_evh_2383909669375364_success_qtmqb6itv =
    new actionTypes.EventHandlerImpl({
      action: act_2383909669375364_emit_sfmiqir83,
      event: 'success',
      displayName: 'emit',
    });
  const act_2383909669375364_reloadSlickgrid_rgjfwu6bm =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2383909669375364_reloadSlickgrid_rgjfwu6bm_getActionArgs,
    });
  const inst_evh_2383909669375364_success_nabt65zlu =
    new actionTypes.EventHandlerImpl({
      action: act_2383909669375364_reloadSlickgrid_rgjfwu6bm,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2383909669375364_request_5yr2g6jlk = new actionTypes.RequestAction({
    actionArgs: act_2383909669375364_request_5yr2g6jlk_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_2383909669375364_success_qtmqb6itv,
      inst_evh_2383909669375364_success_nabt65zlu,
    ],
  });
  const inst_evh_2383909669375364_clickPreventStop_jwqt9rcl9 =
    new actionTypes.EventHandlerImpl({
      action: act_2383909669375364_request_5yr2g6jlk,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_2383909669375364_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2383909669375364_clickPreventStop_jwqt9rcl9],
      event: 'click.prevent.stop',
    });
  const act_2342151935932928_emit_nwqgzgqnz = new actionTypes.EmitAction({
    actionArgs: act_2342151935932928_emit_nwqgzgqnz_getActionArgs,
  });
  const inst_evh_2342151935932928_success_oqtpna58z =
    new actionTypes.EventHandlerImpl({
      action: act_2342151935932928_emit_nwqgzgqnz,
      event: 'success',
      displayName: 'emit',
    });
  const act_2342151935932928_reloadSlickgrid_5qwsxmy12 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2342151935932928_reloadSlickgrid_5qwsxmy12_getActionArgs,
    });
  const inst_evh_2342151935932928_success_x8f5mi4lj =
    new actionTypes.EventHandlerImpl({
      action: act_2342151935932928_reloadSlickgrid_5qwsxmy12,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2342151935932928_request_3v7dv6jzs = new actionTypes.RequestAction({
    actionArgs: act_2342151935932928_request_3v7dv6jzs_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_2342151935932928_success_oqtpna58z,
      inst_evh_2342151935932928_success_x8f5mi4lj,
    ],
  });
  const inst_evh_2342151935932928_clickPreventStop_7ly0p2c43 =
    new actionTypes.EventHandlerImpl({
      action: act_2342151935932928_request_3v7dv6jzs,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_2342151935932928_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2342151935932928_clickPreventStop_7ly0p2c43],
      event: 'click.prevent.stop',
    });
  const act_2498310591257107_emit_1evtxgqge = new actionTypes.EmitAction({
    actionArgs: act_2498310591257107_emit_1evtxgqge_getActionArgs,
  });
  const inst_evh_2498310591257107_success_ec7gtt8xd =
    new actionTypes.EventHandlerImpl({
      action: act_2498310591257107_emit_1evtxgqge,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_2498310591257107_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2498310591257107_success_ec7gtt8xd],
    event: 'success',
  });

  const actions = {
    inst_evh_8358196963355853_updateModelValue_gyy5zixiz,
    inst_evh_8358196963355853_updateModelValue,
    inst_evh_4576710983066270_clickPreventStop_qrbtwjqdn,
    reload: inst_evh_4576710983066270_clickPreventStop_qrbtwjqdn,
    inst_evh_4576710983066270_clickPreventStop,
    inst_evh_2124730035910225_success_jsdoxuazv,
    inst_evh_2124730035910225_success_11v4dpr14,
    inst_evh_2124730035910225_clickPreventStop_6a3050pj9,
    lock: inst_evh_2124730035910225_clickPreventStop_6a3050pj9,
    inst_evh_2124730035910225_clickPreventStop,
    inst_evh_1972474632001399_success_ovmceg5ns,
    inst_evh_1972474632001399_success_ggz3hqip4,
    inst_evh_1972474632001399_clickPreventStop_z12nj6cim,
    inst_evh_1972474632001399_clickPreventStop,
    inst_evh_2383909669375364_success_qtmqb6itv,
    inst_evh_2383909669375364_success_nabt65zlu,
    inst_evh_2383909669375364_clickPreventStop_jwqt9rcl9,
    inst_evh_2383909669375364_clickPreventStop,
    inst_evh_2342151935932928_success_oqtpna58z,
    inst_evh_2342151935932928_success_x8f5mi4lj,
    inst_evh_2342151935932928_clickPreventStop_7ly0p2c43,
    inst_evh_2342151935932928_clickPreventStop,
    inst_evh_2498310591257107_success_ec7gtt8xd,
    inst_evh_2498310591257107_success,
  };

  const evh_8358196963355853_updateModelValue_gyy5zixiz = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8358196963355853_updateModelValue_gyy5zixiz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4576710983066270_clickPreventStop_qrbtwjqdn = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4576710983066270_clickPreventStop_qrbtwjqdn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4576710983066270_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4576710983066270_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2124730035910225_success_jsdoxuazv = async (event, scope) => {
    return await actions.inst_evh_2124730035910225_success_jsdoxuazv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2124730035910225_success_11v4dpr14 = async (event, scope) => {
    return await actions.inst_evh_2124730035910225_success_11v4dpr14.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2124730035910225_clickPreventStop_6a3050pj9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2124730035910225_clickPreventStop_6a3050pj9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2124730035910225_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2124730035910225_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1972474632001399_success_ovmceg5ns = async (event, scope) => {
    return await actions.inst_evh_1972474632001399_success_ovmceg5ns.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1972474632001399_success_ggz3hqip4 = async (event, scope) => {
    return await actions.inst_evh_1972474632001399_success_ggz3hqip4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1972474632001399_clickPreventStop_z12nj6cim = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1972474632001399_clickPreventStop_z12nj6cim.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1972474632001399_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1972474632001399_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2383909669375364_success_qtmqb6itv = async (event, scope) => {
    return await actions.inst_evh_2383909669375364_success_qtmqb6itv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2383909669375364_success_nabt65zlu = async (event, scope) => {
    return await actions.inst_evh_2383909669375364_success_nabt65zlu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2383909669375364_clickPreventStop_jwqt9rcl9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2383909669375364_clickPreventStop_jwqt9rcl9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2383909669375364_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2383909669375364_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2342151935932928_success_oqtpna58z = async (event, scope) => {
    return await actions.inst_evh_2342151935932928_success_oqtpna58z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2342151935932928_success_x8f5mi4lj = async (event, scope) => {
    return await actions.inst_evh_2342151935932928_success_x8f5mi4lj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2342151935932928_clickPreventStop_7ly0p2c43 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2342151935932928_clickPreventStop_7ly0p2c43.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2342151935932928_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2342151935932928_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2498310591257107_success_ec7gtt8xd = async (event, scope) => {
    return await actions.inst_evh_2498310591257107_success_ec7gtt8xd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2498310591257107_success = async (event, scope) => {
    return await actions.inst_evh_2498310591257107_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpText muted :text="resourceTitle" class="mb-0" v-if="resourceTitle">
      </ItpText>
      <ItpText tag="h5" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <ItpBox direction="column" v-if="!entry.isNew && entry.invoice">
      <ItpText
        muted
        :text="pgettext('RegisterEntryHeader', 'Invoice number')"
        class="mb-0"
      >
      </ItpText>
      <ItpText tag="h4" :text="entry.invoice.invoice_number" class="mb-0">
      </ItpText>
    </ItpBox>
    <UcRegisterSelector
      :companyId="entry.version.company_id"
      :modelValue="registerInfo"
      toggleClass="bg-white"
      v-if="entry.isNew && selectRegister"
      @update:modelValue="evh_8358196963355853_updateModelValue($event, {})"
    >
    </UcRegisterSelector>
    <h6
      :title="pgettext('ResourceStatus', 'Locked')"
      class="my-0 align-self-center"
      v-if="entry.isReadOnly"
    >
      <span
        class="badge bg-secondary d-inline-flex align-items-center justify-content-start px-2"
        :style="b_6917646200545441_style"
      >
        <ItpIcon :icon="$config.lockIcon"> </ItpIcon>
        <span class="ms-1">
          {{ pgettext('ResourceStatus', 'Locked') }}
        </span>
      </span>
    </h6>
    <h6
      :title="pgettext('ResourceStatus', 'Void')"
      class="my-0 align-self-center"
      v-if="entry.void"
    >
      <span
        class="badge bg-warning d-inline-flex align-items-center justify-content-start px-2"
        :style="b_6424600534600827_style"
      >
        <ItpIcon icon="eva:slash-fill"> </ItpIcon>
        <span class="ms-1">
          {{ pgettext('ResourceStatus', 'Void') }}
        </span>
      </span>
    </h6>
    <BButtonGroup class="ms-auto">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        class="ms-auto"
        v-if="!entry.isNew"
        @click.prevent.stop="evh_4576710983066270_clickPreventStop($event, {})"
      >
      </ItpButton>
      <BDropdown right variant="light" class="border" v-if="!entry.isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Lock')"
          :tooltip="pgettext('BtnLockTooltip', 'Lock')"
          variant="default"
          v-if="!entry.isNew && entry.meta.can_lock"
          @click.prevent.stop="
            evh_2124730035910225_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Unlock')"
          :tooltip="pgettext('BtnLockTooltip', 'Unlock')"
          variant="default"
          v-if="!entry.isNew && entry.meta.can_unlock"
          @click.prevent.stop="
            evh_1972474632001399_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Invalidate')"
          :tooltip="pgettext('BtnLockTooltip', 'Invalidate')"
          variant="default"
          v-if="!entry.isNew && entry.meta.can_invalidate"
          @click.prevent.stop="
            evh_2383909669375364_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Revoke invalidated')"
          :tooltip="pgettext('BtnLockTooltip', 'Revoke invalidated')"
          variant="default"
          v-if="!entry.isNew && entry.meta.can_revoke_invalidation"
          @click.prevent.stop="
            evh_2342151935932928_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="entry"
          :tooltip="pgettext('Register', 'Delete entry')"
          v-if="!entry.isNew && !entry.isReadOnly"
          @success="evh_2498310591257107_success($event, {})"
        >
        </BtnDeleteResource>
      </BDropdown>
    </BButtonGroup>
  </ItpBox>
</template>
